import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Plus, Trash2, Pencil, X, Check, Star, StarOff, Play, Upload, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyVideo = {
  title: '', video_url: '', thumbnail_url: '', description: '',
  is_live: false, is_featured: false,
};

const extractYouTubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /[?&]v=([\w-]{11})/,
    /youtu\.be\/([\w-]{11})/,
    /youtube\.com\/(?:embed|v|shorts|live)\/([\w-]{11})/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return m[1];
  }
  return null;
};

const autoThumb = (url) => {
  const id = extractYouTubeId(url);
  return id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : '';
};

const isUploadedFile = (url) => !!url && url.includes('/api/files/');

export const VideosTab = ({ token }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyVideo });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deletingId, setDeletingId] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const fileInputRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const showError = (msg) => {
    setErrorMsg(msg);
    setTimeout(() => setErrorMsg(''), 5000);
  };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/videos`, { headers });
      setItems(data);
    } catch (e) {
      showError('Failed to load videos: ' + (e?.response?.data?.detail || e.message));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing('new'); setForm({ ...emptyVideo }); };
  const openEdit = (v) => { setEditing(v.id); setForm({ ...emptyVideo, ...v }); };
  const closeForm = () => { setEditing(null); setForm({ ...emptyVideo }); setUploadProgress(0); };

  const save = async () => {
    if (!form.title || !form.video_url) {
      showError('Title and Video URL are required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.thumbnail_url) {
        payload.thumbnail_url = autoThumb(payload.video_url);
      }
      if (editing === 'new') {
        await axios.post(`${API}/admin/videos`, payload, { headers });
      } else {
        await axios.put(`${API}/admin/videos/${editing}`, payload, { headers });
      }
      await load();
      closeForm();
    } catch (e) {
      showError('Save failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this video? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/admin/videos/${id}`, { headers });
      // Optimistic update — drop from the list immediately so the UI feels snappy.
      setItems((prev) => prev.filter((v) => v.id !== id));
    } catch (e) {
      showError('Delete failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setDeletingId(null);
    }
  };

  const toggleFeatured = async (v) => {
    try {
      await Promise.all(items.filter(i => i.id !== v.id && i.is_featured).map(i =>
        axios.put(`${API}/admin/videos/${i.id}`, { ...i, is_featured: false }, { headers })
      ));
      await axios.put(`${API}/admin/videos/${v.id}`, { ...v, is_featured: !v.is_featured }, { headers });
      await load();
    } catch (e) {
      showError('Failed to update featured: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    const maxSizeMB = 200;
    if (file.size > maxSizeMB * 1024 * 1024) {
      showError(`File too large. Max ${maxSizeMB} MB.`);
      return;
    }
    if (!file.type.startsWith('video/')) {
      showError('Please choose a video file (mp4, webm, mov, etc.).');
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      // Backend returns a relative storage path; build the public URL via /api/files
      const url = `${API}/files/${data.path}`;
      setForm((f) => ({
        ...f,
        video_url: url,
        // Clear YouTube-style auto-thumbnail since this is a self-hosted file.
        thumbnail_url: f.thumbnail_url && !f.thumbnail_url.includes('i.ytimg.com') ? f.thumbnail_url : '',
        title: f.title || file.name.replace(/\.[^.]+$/, ''),
      }));
    } catch (e) {
      showError('Upload failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  const previewId = extractYouTubeId(form.video_url);
  const previewFile = isUploadedFile(form.video_url) ? form.video_url : null;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-2xl font-bold text-white">Videos</h2>
          <p className="font-body text-xs text-white/40 mt-1">
            Upload a video file OR paste a YouTube URL from <a href="https://www.youtube.com/@reddragonpokertour" target="_blank" rel="noreferrer" className="text-primary-main hover:underline">your channel</a>. The video marked <Star className="w-3 h-3 inline -mt-0.5 text-secondary-main" /> "Featured" appears at the top of the home page.
          </p>
        </div>
        <Button data-testid="videos-add-btn" onClick={openCreate} className="bg-primary-main text-black hover:bg-primary-hover font-heading tracking-wider">
          <Plus className="w-4 h-4 mr-2" /> Add Video
        </Button>
      </div>

      {errorMsg && (
        <div data-testid="videos-error" className="mb-4 bg-red-900/30 border border-red-700/50 text-red-300 px-4 py-3 rounded-lg text-sm">
          {errorMsg}
        </div>
      )}

      {editing && (
        <div className="mb-6 rounded-2xl border border-primary-main/30 bg-background-surface p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-bold text-white">{editing === 'new' ? 'New Video' : 'Edit Video'}</h3>
            <button onClick={closeForm} className="text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
          </div>

          {/* Upload tile */}
          <div className="mb-5 rounded-xl border border-dashed border-white/15 bg-white/[0.02] p-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="font-heading text-[11px] tracking-[0.25em] uppercase text-white/60">Upload Video File</p>
                <p className="font-body text-xs text-white/40 mt-1">MP4, WebM, or MOV (up to 200 MB). Auto-fills the URL below.</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                className="hidden"
                data-testid="video-upload-input"
                onChange={(e) => handleFileUpload(e.target.files?.[0])}
              />
              <Button
                data-testid="video-upload-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-secondary-main text-white hover:bg-secondary-main/90 font-heading tracking-wider"
              >
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                {uploading ? `Uploading ${uploadProgress}%` : 'Choose File'}
              </Button>
            </div>
            {uploading && (
              <div className="mt-3 h-1.5 w-full rounded-full bg-white/[0.06] overflow-hidden">
                <div className="h-full bg-secondary-main transition-all" style={{ width: `${uploadProgress}%` }} />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Title" required>
              <Input data-testid="video-field-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="RDPT+ Final Day Highlights" className="bg-background-base border-white/10 text-white" />
            </Field>
            <Field label="YouTube URL or Uploaded File URL" required>
              <Input
                data-testid="video-field-url"
                value={form.video_url}
                onChange={e => setForm({ ...form, video_url: e.target.value })}
                placeholder="https://www.youtube.com/watch?v=...  or  auto-filled after upload"
                className="bg-background-base border-white/10 text-white font-mono text-xs"
              />
            </Field>
            <div className="md:col-span-2">
              <Field label="Description">
                <textarea
                  data-testid="video-field-description"
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  placeholder="Optional caption shown below the player"
                  className="w-full rounded-md border border-white/10 bg-background-base text-white/90 font-body text-sm p-3 focus:outline-none focus:ring-1 focus:ring-primary-main/60"
                />
              </Field>
            </div>
            <Field label="Thumbnail URL (auto-filled from YouTube)">
              <Input data-testid="video-field-thumbnail" value={form.thumbnail_url} onChange={e => setForm({ ...form, thumbnail_url: e.target.value })} placeholder="leave blank for auto" className="bg-background-base border-white/10 text-white" />
            </Field>
            <div className="flex flex-col gap-3 pt-2">
              <div className="flex items-center gap-2">
                <Switch data-testid="video-field-featured" checked={!!form.is_featured} onCheckedChange={v => setForm({ ...form, is_featured: v })} />
                <Label className="text-white/70 text-sm">Featured on home</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch data-testid="video-field-live" checked={!!form.is_live} onCheckedChange={v => setForm({ ...form, is_live: v })} />
                <Label className="text-white/70 text-sm">Live now</Label>
              </div>
            </div>
          </div>

          {(previewId || previewFile) && (
            <div className="mt-5">
              <Label className="font-heading text-[10px] tracking-[0.2em] uppercase text-white/40">Preview</Label>
              <div className="mt-2 aspect-video max-w-xl rounded-lg overflow-hidden border border-white/10 bg-black">
                {previewId ? (
                  <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${previewId}?rel=0`} title="Preview" frameBorder="0" allowFullScreen />
                ) : (
                  <video className="w-full h-full" src={previewFile} controls />
                )}
              </div>
            </div>
          )}

          <div className="mt-5 flex gap-3">
            <Button data-testid="video-form-save" onClick={save} disabled={saving || uploading} className="bg-secondary-main text-white hover:bg-secondary-main/90 font-heading tracking-wider">
              <Check className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button onClick={closeForm} variant="ghost" className="text-white/60 hover:text-white">Cancel</Button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="text-center py-16 border border-white/[0.06] rounded-2xl">
          <p className="font-body text-sm text-white/40">No videos yet. Click "Add Video" to add your first one.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(v => {
            const ytId = extractYouTubeId(v.video_url);
            const thumb = v.thumbnail_url || (ytId ? `https://i.ytimg.com/vi/${ytId}/mqdefault.jpg` : '');
            const isDeleting = deletingId === v.id;
            return (
              <div key={v.id} data-testid={`video-row-${v.id}`} className="flex items-center gap-4 p-3 rounded-xl border border-white/[0.06] bg-white/[0.02]">
                <div className="flex-shrink-0 w-28 aspect-video rounded-md overflow-hidden bg-black/40 relative">
                  {thumb ? <img src={thumb} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full" />}
                  <Play className="absolute inset-0 m-auto w-7 h-7 text-white/85 drop-shadow" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading text-base font-bold text-white truncate">{v.title}</h3>
                    {v.is_featured && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary-main/15 text-secondary-main text-[9px] uppercase tracking-widest font-heading">★ Featured</span>}
                    {v.is_live && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-600/20 text-red-300 text-[9px] uppercase tracking-widest font-heading">● Live</span>}
                    {isUploadedFile(v.video_url) && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 text-white/60 text-[9px] uppercase tracking-widest font-heading">Uploaded</span>}
                  </div>
                  <p className="font-mono text-xs text-white/40 mt-1 truncate">{v.video_url}</p>
                </div>
                <Button data-testid={`video-feature-${v.id}`} onClick={() => toggleFeatured(v)} variant="ghost" className={v.is_featured ? 'text-secondary-main' : 'text-white/40 hover:text-white'}>
                  {v.is_featured ? <Star className="w-4 h-4 fill-current" /> : <StarOff className="w-4 h-4" />}
                </Button>
                <Button data-testid={`video-edit-${v.id}`} onClick={() => openEdit(v)} variant="ghost" className="text-white/60 hover:text-white" disabled={isDeleting}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  data-testid={`video-delete-${v.id}`}
                  onClick={() => remove(v.id)}
                  variant="ghost"
                  className="text-red-400/70 hover:text-red-400"
                  disabled={isDeleting}
                >
                  {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <div>
    <Label className="font-heading text-[10px] tracking-[0.2em] uppercase text-white/50">
      {label}{required && <span className="text-primary-main ml-0.5">*</span>}
    </Label>
    <div className="mt-2">{children}</div>
  </div>
);
